import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import NavigationBar from '../layout/NavigationBar';
import Footer from '../layout/footer';

const WebStoreSettings = () => {
    const [loading, setLoading] = useState(false);
    const [businessId, setBusinessId] = useState('');
    const [uploadLoading, setUploadLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [adminDefaults, setAdminDefaults] = useState({
        banner_slides: [],
        default_categories: [],
        featured_products: [],
        four_column_section: { enabled: false, title: "", columns: [] },
        theme: { primary_color: "#007bff", accent_color: "#28a745", font_family: "Arial, sans-serif" }
    });

    useEffect(() => {
        const business_id = localStorage.getItem('business_id');
        if (business_id) {
            setBusinessId(business_id);
        }
        checkAdminStatus();
        fetchCategories();
        fetchProducts();
    }, []);

    const checkAdminStatus = () => {
        const userType = localStorage.getItem('signup');
        setIsAdmin(userType === 'admin');
        if (userType === 'admin') {
            fetchAdminDefaults();
        }
    };

    const fetchAdminDefaults = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_APIURL}admin/default/sections`);
            const data = await response.json();

            if (data.status === 'success') {
                setAdminDefaults(data.result);
                // Set selected categories and products for UI
                setSelectedCategories(data.result.default_categories || []);
                setSelectedProducts(data.result.featured_products || []);
            }
        } catch (error) {
            console.error('Error fetching admin defaults:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_APIURL}category/list?front_category=true`);
            const data = await response.json();
            if (data.status === 'success') {
                setAvailableCategories(data.result);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchProducts = async () => {
        try {
            // Fetch from a general products endpoint or use a sample business_id
            const sampleBusinessId = '1'; // You can modify this as needed
            const [greetingRes, giftRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_APIURL}greeting/list`),
                fetch(`${process.env.REACT_APP_APIURL}gift/list`)
            ]);

            const greetingData = await greetingRes.json();
            const giftData = await giftRes.json();

            const combinedProducts = [
                ...(giftData.status === 'success' ? giftData.result.map(item => ({ ...item, type: 'gift' })) : []),
                // ...(greetingData.status === 'success' ? greetingData.result.map(item => ({ ...item, type: 'greeting_card' })) : [])
            ];

            setAvailableProducts(combinedProducts);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    const handleAdminDefaultsChange = (section, field, value) => {
        setAdminDefaults(prev => ({
            ...prev,
            [section]: typeof prev[section] === 'object' && !Array.isArray(prev[section])
                ? { ...prev[section], [field]: value }
                : value
        }));
    };

    // Generic file upload function
    const handleFileUpload = async (file, reference = 'event') => {
        if (!file) return null;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('reference', reference);
        formData.append('user_id', businessId);

        try {
            setUploadLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}file/event/upload`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.status === 'success' && data.result.files.length > 0) {
                toast.success('Image uploaded successfully');
                return data.result.files[0].file_url;
            } else {
                toast.error('Failed to upload image');
                return null;
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Error uploading image');
            return null;
        } finally {
            setUploadLoading(false);
        }
    };

    // Banner slide functions
    const addDefaultBanner = () => {
        const newSlide = {
            id: `default-${Date.now()}`,
            image_url: '',
            title: '',
            subtitle: '',
            button_text: '',
            button_link: '',
            active: true
        };

        const updatedSlides = [...adminDefaults.banner_slides, newSlide];
        setAdminDefaults(prev => ({ ...prev, banner_slides: updatedSlides }));
    };

    const updateDefaultBanner = (index, field, value) => {
        const updatedSlides = [...adminDefaults.banner_slides];
        updatedSlides[index][field] = value;
        setAdminDefaults(prev => ({ ...prev, banner_slides: updatedSlides }));
    };

    const removeDefaultBanner = (index) => {
        const updatedSlides = adminDefaults.banner_slides.filter((_, i) => i !== index);
        setAdminDefaults(prev => ({ ...prev, banner_slides: updatedSlides }));
    };

    const handleBannerImageUpload = async (event, index) => {
        const file = event.target.files[0];
        const imageUrl = await handleFileUpload(file, 'banner');
        if (imageUrl) {
            updateDefaultBanner(index, 'image_url', imageUrl);
        }
        event.target.value = '';
    };

    // Category selection functions
    const toggleCategorySelection = (categoryName) => {
        const isCurrentlySelected = selectedCategories.some(selected => selected.category === categoryName);

        let updatedCategories;
        if (isCurrentlySelected) {
            // Remove category
            updatedCategories = selectedCategories.filter(selected => selected.category !== categoryName);
        } else {
            // Add category with all required fields
            updatedCategories = [...selectedCategories, {
                category: categoryName,
                image_url: '',
                description: '',
                price: 0,
                original_price: 0,
                offer_percentage: 0,
                offer_text: ''
            }];
        }

        setSelectedCategories(updatedCategories);
        setAdminDefaults(prev => ({ ...prev, default_categories: updatedCategories }));
    };
    const updateCategoryField = (categoryName, field, value) => {
        const updatedCategories = selectedCategories.map(selected => {
            if (selected.category === categoryName) {
                return { ...selected, [field]: value };
            }
            return selected;
        });

        setSelectedCategories(updatedCategories);
        setAdminDefaults(prev => ({ ...prev, default_categories: updatedCategories }));
    };

    const updateCategoryImage = (categoryName, imageUrl) => {
        const updatedCategories = selectedCategories.map(selected => {
            if (typeof selected === 'object' && selected.category === categoryName) {
                return { ...selected, image_url: imageUrl };
            } else if (selected === categoryName) {
                return { category: categoryName, image_url: imageUrl };
            }
            return selected;
        });

        setSelectedCategories(updatedCategories);
        setAdminDefaults(prev => ({ ...prev, default_categories: updatedCategories }));
    };

    const handleSelectedCategoryImageUpload = async (event, categoryName) => {
        const file = event.target.files[0];
        const imageUrl = await handleFileUpload(file, 'category');
        if (imageUrl) {
            updateCategoryField(categoryName, 'image_url', imageUrl);
        }
        event.target.value = '';
    };

    // Product selection functions
    const toggleProductSelection = (productId) => {
        const updatedProducts = selectedProducts.includes(productId)
            ? selectedProducts.filter(id => id !== productId)
            : [...selectedProducts, productId];

        setSelectedProducts(updatedProducts);
        setAdminDefaults(prev => ({ ...prev, featured_products: updatedProducts }));
    };

    // Product functions - removed since we're using selection format now

    // Four column section functions
    const addColumn = () => {
        if (adminDefaults.four_column_section.columns.length < 8) {
            const newColumn = {
                title: '',
                items: [
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' }
                ]
            };
            const updatedColumns = [...adminDefaults.four_column_section.columns, newColumn];
            setAdminDefaults(prev => ({
                ...prev,
                four_column_section: { ...prev.four_column_section, columns: updatedColumns }
            }));
        }
    };

    const removeColumn = (columnIndex) => {
        const updatedColumns = adminDefaults.four_column_section.columns.filter((_, index) => index !== columnIndex);
        setAdminDefaults(prev => ({
            ...prev,
            four_column_section: { ...prev.four_column_section, columns: updatedColumns }
        }));
    };

    const updateColumnTitle = (columnIndex, title) => {
        const updatedColumns = [...adminDefaults.four_column_section.columns];
        if (!updatedColumns[columnIndex]) {
            updatedColumns[columnIndex] = { title: '', items: [] };
        }
        updatedColumns[columnIndex].title = title;
        setAdminDefaults(prev => ({
            ...prev,
            four_column_section: { ...prev.four_column_section, columns: updatedColumns }
        }));
    };

    const updateColumnImage = (columnIndex, imageIndex, field, value) => {
        const updatedColumns = [...adminDefaults.four_column_section.columns];
        if (!updatedColumns[columnIndex]) {
            updatedColumns[columnIndex] = { title: '', items: [] };
        }
        if (!updatedColumns[columnIndex].items[imageIndex]) {
            updatedColumns[columnIndex].items[imageIndex] = { image_url: '', description: '', link_url: '' };
        }
        updatedColumns[columnIndex].items[imageIndex][field] = value;
        setAdminDefaults(prev => ({
            ...prev,
            four_column_section: { ...prev.four_column_section, columns: updatedColumns }
        }));
    };

    const handleColumnImageUpload = async (event, columnIndex, imageIndex) => {
        const file = event.target.files[0];
        const imageUrl = await handleFileUpload(file, 'column');
        if (imageUrl) {
            updateColumnImage(columnIndex, imageIndex, 'image_url', imageUrl);
        }
        event.target.value = '';
    };

    const saveAdminDefaults = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}admin/default/sections`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(adminDefaults)
            });

            const data = await response.json();
            if (data.status === 'success') {
                toast.success('Admin defaults updated successfully');
            } else {
                toast.error('Failed to update admin defaults');
            }
        } catch (error) {
            console.error('Error saving admin defaults:', error);
            toast.error('Error saving admin defaults');
        } finally {
            setLoading(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="wrapper">
                <header>
                    <NavigationBar />
                </header>
                <div className="content-wrapper">
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="alert alert-warning text-center">
                                <h4>Access Denied</h4>
                                <p>This section is only available for admin users.</p>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="wrapper">
            <header>
                <NavigationBar />
            </header>
            <div className="content-wrapper">
                {/* Content Header */}
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">Admin: Configure Homepage Defaults</h1>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card border-warning">
                                    <div className="card-header bg-warning">
                                        <h3 className="card-title">
                                            <i className="fas fa-crown"></i> Admin: Configure Homepage Defaults
                                        </h3>
                                        <p className="card-text mb-0">These settings will be used as defaults for the main EventsEasy homepage</p>
                                    </div>
                                    <div className="card-body">

                                        {/* Admin Default Banners */}
                                        <div className="mb-4">
                                            <h5>Default Banner Slides</h5>
                                            <button className="btn btn-success mb-3" onClick={addDefaultBanner}>
                                                <i className="fas fa-plus"></i> Add Default Banner
                                            </button>

                                            {adminDefaults.banner_slides.map((slide, index) => (
                                                <div key={slide.id || index} className="card mb-2">
                                                    <div className="card-body">
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    <label>Banner Image</label>
                                                                    <div className="input-group">
                                                                        <input
                                                                            type="url"
                                                                            className="form-control"
                                                                            placeholder="Image URL"
                                                                            value={slide.image_url}
                                                                            onChange={(e) => updateDefaultBanner(index, 'image_url', e.target.value)}
                                                                        />
                                                                        <div className="input-group-append">
                                                                            <label className="btn btn-outline-secondary" htmlFor={`bannerUpload-${index}`}>
                                                                                {uploadLoading ? (
                                                                                    <i className="fas fa-spinner fa-spin"></i>
                                                                                ) : (
                                                                                    <i className="fas fa-upload"></i>
                                                                                )}
                                                                                Upload
                                                                            </label>
                                                                            <input
                                                                                id={`bannerUpload-${index}`}
                                                                                type="file"
                                                                                accept="image/*"
                                                                                onChange={(e) => handleBannerImageUpload(e, index)}
                                                                                style={{ display: 'none' }}
                                                                                disabled={uploadLoading}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    {slide.image_url && (
                                                                        <img
                                                                            src={slide.image_url}
                                                                            alt="Banner preview"
                                                                            style={{ width: '100%', height: '100px', objectFit: 'cover', marginTop: '8px', borderRadius: '4px' }}
                                                                        />
                                                                    )}
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    className="form-control mb-2"
                                                                    placeholder="Title"
                                                                    value={slide.title}
                                                                    onChange={(e) => updateDefaultBanner(index, 'title', e.target.value)}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Subtitle"
                                                                    value={slide.subtitle}
                                                                    onChange={(e) => updateDefaultBanner(index, 'subtitle', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="col-md-6">
                                                                <input
                                                                    type="text"
                                                                    className="form-control mb-2"
                                                                    placeholder="Button Text"
                                                                    value={slide.button_text}
                                                                    onChange={(e) => updateDefaultBanner(index, 'button_text', e.target.value)}
                                                                />
                                                                <input
                                                                    type="url"
                                                                    className="form-control mb-2"
                                                                    placeholder="Button Link"
                                                                    value={slide.button_link}
                                                                    onChange={(e) => updateDefaultBanner(index, 'button_link', e.target.value)}
                                                                />
                                                                <button
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => removeDefaultBanner(index)}
                                                                >
                                                                    <i className="fas fa-trash"></i> Remove
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Admin Default Categories */}
                                        <div className="mb-4">
                                            <h5>Default Categories</h5>
                                            <p className="text-muted">Select categories to display on the homepage with pricing and offer details</p>

                                            {availableCategories.length === 0 ? (
                                                <div className="alert alert-warning text-center" role="alert">
                                                    <i className="fas fa-exclamation-triangle mb-2"></i>
                                                    <h6>No Categories Found</h6>
                                                    <p className="mb-0">Please create categories first to select them for homepage display.</p>
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    {availableCategories.map((category) => {
                                                        const isSelected = selectedCategories.some(selected => selected.category === category.category);
                                                        const selectedCategoryData = selectedCategories.find(selected => selected.category === category.category);

                                                        return (
                                                            <div key={category.category} className="col-md-6 mb-4">
                                                                <div className="card" style={{ border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6' }}>
                                                                    <div className="card-body">
                                                                        <div className="custom-control custom-checkbox mb-3">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="custom-control-input"
                                                                                id={`admin-category-${category.category}`}
                                                                                checked={isSelected}
                                                                                onChange={() => toggleCategorySelection(category.category)}
                                                                            />
                                                                            <label
                                                                                className="custom-control-label fw-bold"
                                                                                htmlFor={`admin-category-${category.category}`}
                                                                            >
                                                                                {category.category}
                                                                            </label>
                                                                        </div>

                                                                        {isSelected && (
                                                                            <div className="category-details-section">
                                                                                {/* Image Section */}
                                                                                <div className="form-group mb-3">
                                                                                    <label className="form-label">Category Image</label>
                                                                                    <div className="input-group mb-2">
                                                                                        <input
                                                                                            type="url"
                                                                                            className="form-control form-control-sm"
                                                                                            placeholder="Enter image URL"
                                                                                            value={selectedCategoryData?.image_url || ''}
                                                                                            onChange={(e) => updateCategoryField(category.category, 'image_url', e.target.value)}
                                                                                        />
                                                                                        <div className="input-group-append">
                                                                                            <label className="btn btn-outline-secondary btn-sm" htmlFor={`adminCategoryUpload-${category.category}`}>
                                                                                                {uploadLoading ? (
                                                                                                    <i className="fas fa-spinner fa-spin"></i>
                                                                                                ) : (
                                                                                                    <i className="fas fa-upload"></i>
                                                                                                )}
                                                                                                Upload
                                                                                            </label>
                                                                                            <input
                                                                                                id={`adminCategoryUpload-${category.category}`}
                                                                                                type="file"
                                                                                                accept="image/*"
                                                                                                onChange={(e) => handleSelectedCategoryImageUpload(e, category.category)}
                                                                                                style={{ display: 'none' }}
                                                                                                disabled={uploadLoading}
                                                                                            />
                                                                                        </div>
                                                                                    </div>

                                                                                    {selectedCategoryData?.image_url && (
                                                                                        <div className="mb-2">
                                                                                            <img
                                                                                                src={selectedCategoryData.image_url}
                                                                                                alt={category.category}
                                                                                                style={{
                                                                                                    width: '100%',
                                                                                                    height: '120px',
                                                                                                    objectFit: 'cover',
                                                                                                    borderRadius: '8px',
                                                                                                    border: '1px solid #dee2e6'
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                    <small className="text-muted d-block">
                                                                                        Recommended size: 300x200px
                                                                                    </small>
                                                                                </div>

                                                                                {/* Description */}
                                                                                <div className="form-group mb-3">
                                                                                    <label className="form-label">Description</label>
                                                                                    <textarea
                                                                                        className="form-control form-control-sm"
                                                                                        rows="2"
                                                                                        placeholder="Enter category description"
                                                                                        value={selectedCategoryData?.description || ''}
                                                                                        onChange={(e) => updateCategoryField(category.category, 'description', e.target.value)}
                                                                                    />
                                                                                </div>

                                                                                {/* Pricing Section */}
                                                                                <div className="row mb-3">
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label className="form-label">Current Price (₹)</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="0"
                                                                                                min="0"
                                                                                                step="0.01"
                                                                                                value={selectedCategoryData?.price || 0}
                                                                                                onChange={(e) => updateCategoryField(category.category, 'price', parseFloat(e.target.value) || 0)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label className="form-label">Original Price (₹)</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="0"
                                                                                                min="0"
                                                                                                step="0.01"
                                                                                                value={selectedCategoryData?.original_price || 0}
                                                                                                onChange={(e) => updateCategoryField(category.category, 'original_price', parseFloat(e.target.value) || 0)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                {/* Offer Section */}
                                                                                <div className="row mb-3">
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label className="form-label">Offer Percentage (%)</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="0"
                                                                                                min="0"
                                                                                                max="100"
                                                                                                value={selectedCategoryData?.offer_percentage || 0}
                                                                                                onChange={(e) => updateCategoryField(category.category, 'offer_percentage', parseInt(e.target.value) || 0)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label className="form-label">Offer Text</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="e.g., Limited Time Offer"
                                                                                                value={selectedCategoryData?.offer_text || ''}
                                                                                                onChange={(e) => updateCategoryField(category.category, 'offer_text', e.target.value)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* Admin Default Featured Products */}
                                        <div className="mb-4">
                                            <h5>Default Featured Products</h5>
                                            <p className="text-muted">Select products to feature on the homepage (Max 8)</p>

                                            {availableProducts.length === 0 ? (
                                                <div className="alert alert-warning text-center" role="alert">
                                                    <i className="fas fa-exclamation-triangle mb-2"></i>
                                                    <h6>No Products Found</h6>
                                                    <p className="mb-0">Please create products first to feature them on the homepage.</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="row" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                        {availableProducts.map((product) => (
                                                            <div key={product._id} className="col-md-3 mb-3">
                                                                <div className="card" style={{ border: selectedProducts.includes(product._id) ? '2px solid #007bff' : '1px solid #dee2e6' }}>
                                                                    <img
                                                                        src={product.url || product.image_url}
                                                                        className="card-img-top"
                                                                        style={{ height: '120px', objectFit: 'cover' }}
                                                                        alt={product.name}
                                                                        onError={(e) => {
                                                                            e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                                                                        }}
                                                                    />
                                                                    <div className="card-body p-2">
                                                                        <h6 className="card-title" style={{ fontSize: '12px' }}>
                                                                            {product.name}
                                                                        </h6>
                                                                        <p className="text-muted" style={{ fontSize: '10px' }}>
                                                                            ₹{product.price || 0} | {product.type}
                                                                        </p>
                                                                    </div>
                                                                    <div className="card-footer p-2">
                                                                        <div className="custom-control custom-checkbox">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="custom-control-input"
                                                                                id={`admin-product-${product._id}`}
                                                                                checked={selectedProducts.includes(product._id)}
                                                                                onChange={() => toggleProductSelection(product._id)}
                                                                                disabled={!selectedProducts.includes(product._id) && selectedProducts.length >= 8}
                                                                            />
                                                                            <label
                                                                                className="custom-control-label"
                                                                                htmlFor={`admin-product-${product._id}`}
                                                                            >
                                                                                Feature
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <small className="text-muted">
                                                        Selected: {selectedProducts.length}/8 products
                                                    </small>
                                                </>
                                            )}
                                        </div>

                                        {/* Four Column Section */}
                                        <div className="mb-4">
                                            <h5>Four Column Display Section</h5>
                                            <div className="custom-control custom-switch mb-3">
                                                <input
                                                    type="checkbox"
                                                    className="custom-control-input"
                                                    id="fourColumnSwitch"
                                                    checked={adminDefaults.four_column_section.enabled}
                                                    onChange={(e) => setAdminDefaults(prev => ({
                                                        ...prev,
                                                        four_column_section: { ...prev.four_column_section, enabled: e.target.checked }
                                                    }))}
                                                />
                                                <label className="custom-control-label" htmlFor="fourColumnSwitch">Enable Four Column Section</label>
                                            </div>

                                            {adminDefaults.four_column_section.enabled && (
                                                <>
                                                    <div className="form-group">
                                                        <label>Section Title</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={adminDefaults.four_column_section.title}
                                                            onChange={(e) => setAdminDefaults(prev => ({
                                                                ...prev,
                                                                four_column_section: { ...prev.four_column_section, title: e.target.value }
                                                            }))}
                                                            placeholder="e.g., Shop by Category"
                                                        />
                                                    </div>

                                                    <div className="mb-3">
                                                        <button
                                                            className="btn btn-success"
                                                            onClick={addColumn}
                                                            disabled={adminDefaults.four_column_section.columns.length >= 8}
                                                        >
                                                            <i className="fas fa-plus"></i> Add Column (Max 8)
                                                        </button>
                                                    </div>

                                                    <div className="row">
                                                        {adminDefaults.four_column_section.columns.map((column, columnIndex) => (
                                                            <div key={columnIndex} className="col-md-3 mb-4">
                                                                <div className="card border-primary">
                                                                    <div className="card-header d-flex justify-content-between align-items-center">
                                                                        <h6 className="mb-0">Column {columnIndex + 1}</h6>
                                                                        <button
                                                                            className="btn btn-danger btn-sm"
                                                                            onClick={() => removeColumn(columnIndex)}
                                                                        >
                                                                            <i className="fas fa-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                    <div className="card-body">
                                                                        {/* Column Title */}
                                                                        <div className="form-group">
                                                                            <label>Column Title</label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={column.title || ''}
                                                                                onChange={(e) => updateColumnTitle(columnIndex, e.target.value)}
                                                                                placeholder="e.g., TVs & Electronics"
                                                                            />
                                                                        </div>

                                                                        {/* Four Images for this column */}
                                                                        {[0, 1, 2, 3].map((imageIndex) => (
                                                                            <div key={imageIndex} className="mb-3 p-2 border rounded">
                                                                                <label className="form-label">Item {imageIndex + 1}</label>

                                                                                {/* Image Upload */}
                                                                                <div className="form-group">
                                                                                    <label>Image</label>
                                                                                    <div className="input-group">
                                                                                        <input
                                                                                            type="url"
                                                                                            className="form-control form-control-sm"
                                                                                            placeholder="Image URL"
                                                                                            value={column.items?.[imageIndex]?.image_url || ''}
                                                                                            onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'image_url', e.target.value)}
                                                                                        />
                                                                                        <div className="input-group-append">
                                                                                            <label className="btn btn-outline-secondary btn-sm" htmlFor={`columnUpload-${columnIndex}-${imageIndex}`}>
                                                                                                {uploadLoading ? (
                                                                                                    <i className="fas fa-spinner fa-spin"></i>
                                                                                                ) : (
                                                                                                    <i className="fas fa-upload"></i>
                                                                                                )}
                                                                                            </label>
                                                                                            <input
                                                                                                id={`columnUpload-${columnIndex}-${imageIndex}`}
                                                                                                type="file"
                                                                                                accept="image/*"
                                                                                                onChange={(e) => handleColumnImageUpload(e, columnIndex, imageIndex)}
                                                                                                style={{ display: 'none' }}
                                                                                                disabled={uploadLoading}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    {column.items?.[imageIndex]?.image_url && (
                                                                                        <img
                                                                                            src={column.items[imageIndex].image_url}
                                                                                            alt={`Column ${columnIndex + 1} Item ${imageIndex + 1}`}
                                                                                            style={{
                                                                                                width: '100%',
                                                                                                height: '60px',
                                                                                                objectFit: 'cover',
                                                                                                borderRadius: '4px',
                                                                                                marginTop: '8px'
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </div>

                                                                                {/* Description Input */}
                                                                                <div className="form-group">
                                                                                    <label>Description</label>
                                                                                    <input
                                                                                        type="text"
                                                                                        className="form-control form-control-sm"
                                                                                        placeholder="Description"
                                                                                        value={column.items?.[imageIndex]?.description || ''}
                                                                                        onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'description', e.target.value)}
                                                                                    />
                                                                                </div>

                                                                                {/* Link URL Input */}
                                                                                <div className="form-group">
                                                                                    <label>Link URL</label>
                                                                                    <input
                                                                                        type="url"
                                                                                        className="form-control form-control-sm"
                                                                                        placeholder="Link URL (optional)"
                                                                                        value={column.items?.[imageIndex]?.link_url || ''}
                                                                                        onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'link_url', e.target.value)}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Admin Theme Settings */}
                                        <div className="mb-4">
                                            <h5>Default Theme Colors</h5>
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <div className="form-group">
                                                        <label>Primary Color</label>
                                                        <input
                                                            type="color"
                                                            className="form-control"
                                                            value={adminDefaults.theme.primary_color}
                                                            onChange={(e) => handleAdminDefaultsChange('theme', 'primary_color', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="form-group">
                                                        <label>Accent Color</label>
                                                        <input
                                                            type="color"
                                                            className="form-control"
                                                            value={adminDefaults.theme.accent_color}
                                                            onChange={(e) => handleAdminDefaultsChange('theme', 'accent_color', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="form-group">
                                                        <label>Font Family</label>
                                                        <select
                                                            className="form-control"
                                                            value={adminDefaults.theme.font_family}
                                                            onChange={(e) => handleAdminDefaultsChange('theme', 'font_family', e.target.value)}
                                                        >
                                                            <option value="Arial, sans-serif">Arial</option>
                                                            <option value="Georgia, serif">Georgia</option>
                                                            <option value="Helvetica, sans-serif">Helvetica</option>
                                                            <option value="Times New Roman, serif">Times New Roman</option>
                                                            <option value="Verdana, sans-serif">Verdana</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Save Admin Defaults Button */}
                                        <button
                                            className="btn btn-warning btn-lg"
                                            onClick={saveAdminDefaults}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-save"></i> {loading ? 'Saving...' : 'Save Admin Defaults'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer />
        </div>
    );
};

export default WebStoreSettings;